import * as perspectivesEditorActions from 'perspective/actions';

import {
  currentlySelectedSavedPerspectiveValues$,
  filters$,
  perspectiveEditorFormattingOptions$,
  perspectiveEditorGroupingOptions$,
  savedPerspectiveOptions$,
} from 'streams/perspectiveEditorData';

import type { TargetPortalConfig } from '@ardoq/post-message-bridge';
import { perspectivesRelatedData$ } from 'streams/perspectiveEditorData/perspectivesRelatedData$';
import { setIsUpdatingAppState } from 'isUpdatingAppState$';
import { currentViewId$ } from '../../streams/currentViewId$';
import { locale$ } from '../../streams/locale$';
import { setI18nLocale } from 'streams/i18nLocale$';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';
import { distinctUntilChanged, map } from 'rxjs';
import { PersonalSetting } from '@ardoq/api-types';
import { userOperations } from '@ardoq/core';

type StreamSubscriptions = NonNullable<
  TargetPortalConfig['streamSubscriptions']
>;

/**
 * Subscriptions that are valid in both the popout perspective editor (over
 * the postMessageBridge) and the sidebar perspective editor's main-app reuse
 * via `usePerspectiveEditorPortalSubscription`. Add a subscription here only
 * if dispatching its action locally in the main app is also a no-op /
 * idempotent for canonical app state.
 *
 * Popout-only subscriptions (e.g. `setI18nLocale`) live below in
 * `perspectivesEditorTargetPortalConfig.streamSubscriptions` instead.
 */
export const sharedPerspectiveStreamSubscriptions: StreamSubscriptions = [
  {
    stream: savedPerspectiveOptions$,
    action: perspectivesEditorActions.notifySavedPerspectivesOptionsChanged,
  },
  {
    stream: currentlySelectedSavedPerspectiveValues$,
    action: perspectivesEditorActions.notifySavedPerspectiveSelected,
  },
  {
    stream: perspectiveEditorGroupingOptions$,
    action: perspectivesEditorActions.notifyGroupingOptionsChanged,
  },
  {
    stream: perspectiveEditorFormattingOptions$,
    action: perspectivesEditorActions.notifyFormattingOptionsChanged,
  },
  {
    stream: filters$,
    action: perspectivesEditorActions.notifyFiltersChanged,
  },
  {
    stream: perspectivesRelatedData$,
    action: perspectivesEditorActions.notifyPerspectivesRelatedDataChanged,
  },
  {
    stream: currentViewId$,
    action: perspectivesEditorActions.notifyViewIdChanged,
  },
  {
    stream: locale$.pipe(map(locale => locale.code)),
    action: perspectivesEditorActions.notifyLocaleChanged,
  },
];

// Configures `ardoq-front -> standalone (PerspectiveEditor)` communication
export const perspectivesEditorTargetPortalConfig: TargetPortalConfig = {
  id: 'PERSPECTIVES_EDITOR',
  type: 'TARGET_PORTAL',
  actions: [
    perspectivesEditorActions.notifyDeleteSavedPerspectiveFailed,
    perspectivesEditorActions.notifyDeletingPerspectiveFinished,
    perspectivesEditorActions.notifyRenameSavedPerspectiveFinished,
    perspectivesEditorActions.notifyRenameSavedPerspectiveFailed,
    perspectivesEditorActions.notifySavePerspectiveFinished,
    perspectivesEditorActions.notifySavePerspectiveFailed,
    perspectivesEditorActions.notifySaveAsNewPerspectiveFinished,
    perspectivesEditorActions.notifySaveAsNewPerspectiveFailed,
    perspectivesEditorActions.notifyCreateNewPerspectiveFinished,
    perspectivesEditorActions.notifyCreateNewPerspectiveFailed,
    perspectivesEditorActions.notifyPerspectiveCleared,
    perspectivesEditorActions.notifyPerspectiveApplied,
    setIsUpdatingAppState,
  ],
  streamSubscriptions: [
    ...sharedPerspectiveStreamSubscriptions,
    {
      // Popout-only: pump the user's i18n language tag across the bridge so
      // the perspective editor popout (which has no current-user stream of
      // its own) can look up its already-bundled translations locally via
      // `LOCALE_BUNDLES`. Sending just the language avoids a stale-bundle
      // race between `loadedCurrentUser$` and `i18nLocale$` and keeps the
      // postMessage payload small. See ARD-31668. Do NOT add to
      // `sharedPerspectiveStreamSubscriptions` — the main app already keeps
      // `i18nLocale$` in sync via `translationSyncRoutine`, and dispatching
      // `setI18nLocale` there would overwrite the canonical locale state.
      stream: loadedCurrentUser$.pipe(
        map(user => ({
          language:
            userOperations.getPersonalSetting(user, PersonalSetting.LANGUAGE) ??
            'en',
        })),
        distinctUntilChanged((a, b) => a.language === b.language)
      ),
      action: setI18nLocale,
    },
  ],
};
